import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"; // ✅ 변경
import { cn } from "@/shared/libs/utils";
import type { DateRange } from "@/shared/types";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";

type ActiveField = "from" | "to";

export function DateRangePicker({
    value,
    onChange,
}: {
    value: DateRange;
    onChange: (next: DateRange) => void;
}) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState<ActiveField>("from");

    useEffect(() => {
        if (open) setActive("from");
    }, [open]);

    const triggerLabel = useMemo(() => {
        if (value.from && value.to) return `${format(value.from, "yyyy-MM-dd")} ~ ${format(value.to, "yyyy-MM-dd")}`;
        return "날짜 범위를 선택하세요";
    }, [value.from, value.to]);

    const fromText = useMemo(() => (value.from ? format(value.from, "MMM d, yyyy") : ""), [value.from]);
    const toText = useMemo(() => (value.to ? format(value.to, "MMM d, yyyy") : ""), [value.to]);

    const pickDay = (day: Date) => {
        if (active === "from") {
            const nextFrom = day;
            const nextTo = value.to && value.to >= nextFrom ? value.to : undefined;
            onChange({ from: nextFrom, to: nextTo });
            setActive("to");
            return;
        }

        // active === "to"
        if (!value.from) {
            onChange({ from: day, to: undefined });
            return;
        }

        const from = value.from;
        const to = day;
        if (to < from) {
            onChange({ from: to, to: from });
        } else {
            onChange({ from, to });
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                    {triggerLabel}
                </Button>
            </PopoverTrigger>

            <PopoverContent className="p-0 sm:max-w-full">
                {/* 상단 입력 박스 */}
                <div className="border-b bg-muted/30 p-3">
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setActive("from")}
                            className={cn(
                                "h-9 rounded-md border bg-background px-3 text-left text-sm",
                                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                                active === "from" && "ring-2 ring-ring ring-offset-2"
                            )}
                        >
                            {fromText || <span className="text-muted-foreground">Start date</span>}
                        </button>

                        <button
                            type="button"
                            onClick={() => setActive("to")}
                            className={cn(
                                "h-9 rounded-md border bg-background px-3 text-left text-sm",
                                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                                active === "to" && "ring-2 ring-ring ring-offset-2"
                            )}
                        >
                            {toText || <span className="text-muted-foreground">End date</span>}
                        </button>
                    </div>
                </div>

                {/* 달력 영역 */}
                <div className="flex justify-center p-1">
                    <div className="flex justify-center p-2">
                        <Calendar
                            mode="range"
                            selected={value}
                            onDayClick={(day) => pickDay(day)}
                            defaultMonth={value.from ?? new Date()}
                            numberOfMonths={1}
                            showOutsideDays
                            fixedWeeks
                        />
                    </div>


                </div>
                <div className="border-t p-2">
                    <div className="flex justify-center">

                        <Button type="button" className="w-50% h-full max-w-[100px]" onClick={() => setOpen(false)}>
                            선택
                        </Button>

                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}